import axios from "axios";
import {
  ApiServiceConstructor,
  ApiServiceGetRequest,
  ApiServicePostRequest,
} from "./interface/";

export class ApiService {
  localApiBaseUrl = "http://localhost:8002/v1/admin";
  liveApiBaseUrl = import.meta.env.VITE_NEXT_PUBLIC_GMIND_BASEURL || "https://golive.gmind.ai/v1/admin";
  appApiBaseUrl = "";
  unauthorizedPage: undefined | string = undefined;

  constructor(construct: ApiServiceConstructor) {
    const { liveOrLocal, unauthorizedPage } = construct;

    this.appApiBaseUrl =
      liveOrLocal === 1 ? this.localApiBaseUrl : this.liveApiBaseUrl;
    this.unauthorizedPage = unauthorizedPage;
  }

  logout = (nav = 1) => {
    try {
      localStorage.removeItem("token");

      if (nav === 1 && this.unauthorizedPage) {
        // Navigate to unauthorized page
        this.navigateToPage(this.unauthorizedPage, true);
      }
    } catch (error: any) {
      const message = error?.message || "Error processing request";
    }
  };

  navigateToPage = (href: string, isReplace = false) => {
    // We can't use React Router hooks inside a class
    // Instead, we'll handle navigation by changing window.location
    if (isReplace) {
      window.location.replace(href);
    } else {
      window.location.href = href;
    }
  };

  postRequest = async <S, T>(params: ApiServicePostRequest<S, T>) => {
    const {
      postObject,
      apiPath,
      headers,
      load = 1,
      nav = 0,
      method = "POST",
      showSuccess = true,
      showErrror = true,
      onFinallyCallBack,
      loadingCallBack,
      successCallBack,
      errorCallBack,
    } = params;

    // content type 1 - content type js 2 - formdata
    // let data = new FormData();
    // for (const key in postObject) {
    //   data.append(key, postObject[key]);
    // }
    const options = {
      method: method,
      headers,
      url: `${this.appApiBaseUrl}${apiPath}`,
      data: postObject,
    };
    try {
      if (load == 1) {
        if (typeof loadingCallBack === "function") {
          loadingCallBack();
        }
      }
      const response = await axios(options);
      if (typeof successCallBack === "function") {
        successCallBack(response.data);
      }
    } catch (error: any) {
      if (error.response) {
        if (error.response.status == 400) {
          const errorValue = error.response.data;
          if (typeof errorCallBack === "function") {
            errorCallBack({
              errorData: errorValue,
            });
          }
          return;
        }

        if (error.response.status == 403) {
          const errorValue = error.response.data;
          if (typeof errorCallBack === "function") {
            errorCallBack({
              errorData: errorValue,
            });
          }
          return;
        }

        if (error.response.status == 404) {
          const errorValue = error.response.data;
          if (typeof errorCallBack === "function") {
            errorCallBack({
              errorData: errorValue,
            });
          }
          return;
        }

        if (error.response.status == 401) {
          const errorValue = error.response.data;
          if (typeof errorCallBack === "function") {
            errorCallBack({
              errorData: errorValue,
            });
          }
          this.logout(nav);
        }
        if (error.response.status == 405) {
          const errorValue = error.response.data;
          if (typeof errorCallBack === "function") {
            errorCallBack({
              errorData: errorValue,
            });
          }
        }
        if (error.response.status == 422) {
          const errorValue = error.response.data;
          if (typeof errorCallBack === "function") {
            errorCallBack({
              errorData: errorValue,
            });
          }
        }
        if (error.response.status == 500) {
          const errorValue = error.response.data;
          if (typeof errorCallBack === "function") {
            errorCallBack({
              errorData: errorValue,
            });
          }
        }
      }
      const errorMsg = error.message || "Error processing request";
      if (typeof errorCallBack === "function") {
        errorCallBack({
          errorMsg,
          showErrror,
          errorData: error.response?.data,
        });
      }
    } finally {
      if (typeof onFinallyCallBack === "function") {
        onFinallyCallBack();
      }
    }
  };

  getRequest = async <S, F>(params: ApiServiceGetRequest<S, F>) => {
    const {
      apiPath,
      headers,
      load = 1,
      nav = 0,
      authorized = true,
      onFinallyCallBack,
      loadingCallBack,
      successCallBack,
      errorCallBack,
    } = params;
    const options = {
      method: "GET",
      headers,
      url: `${this.appApiBaseUrl}${apiPath}`,
    };
    try {
      if (load == 1) {
        if (typeof loadingCallBack === "function") {
          loadingCallBack();
        }
      }
      const response = await axios(options);
      const successData = response.data;
      if (typeof successCallBack === "function") {
        successCallBack(successData);
      }
    } catch (error: any) {
      if (error.response) {
        if (error.response.status == 400) {
          const errorValue = error.response.data;
          if (typeof errorCallBack === "function") {
            errorCallBack({
              errorData: errorValue,
            });
          }
          return;
        }
        if (error.response.status == 401) {
          if (authorized) {
            const errorValue = error.response.data;
            if (typeof errorCallBack === "function") {
              errorCallBack({
                errorData: errorValue,
              });
            }
            this.logout(nav);
          }
        }
        if (error.response.status == 404) {
            const errorValue = error.response.data;
            if ( typeof errorCallBack === 'function' ) {
                errorCallBack({
                    errorData: errorValue
                });
            }
        }

        if (error.response.status == 403) {
            const errorValue = error.response.data;
            if ( typeof errorCallBack === 'function' ) {
                errorCallBack({
                    errorData: errorValue
                });
            }
        }

        if (error.response.status == 405) {
            const errorValue = error.response.data;
            if ( typeof errorCallBack === 'function' ) {
                errorCallBack({
                    errorData: errorValue
                });
            }
        }

        if (error.response.status == 413) {
            const errorValue = error.response.data;
            if ( typeof errorCallBack === 'function' ) {
                errorCallBack({
                    errorData: errorValue
                });
            }
        }

        if (error.response.status == 422) {
            const errorValue = error.response.data;
            if ( typeof errorCallBack === 'function' ) {
                errorCallBack({
                    errorData: errorValue
                });
            }
        }

        if (error.response.status == 500) {
            const errorValue = error.response.data;
            if ( typeof errorCallBack === 'function' ) {
                errorCallBack({
                    errorData: errorValue
                });
            }
        }
      } else {
        const errorMsg = error.message || "Error processing request";
        if (typeof errorCallBack === "function") {
          errorCallBack({
            errorMsg,
          });
        }
      }
    } finally {
      if (typeof onFinallyCallBack === "function") {
        onFinallyCallBack();
      }
    }
  };
}
